/**
 * Depth-buffer filtering, in metres. Invalid pixels stay 0.
 *
 * Preallocated scratch buffers are reused across frames and written in place:
 * this runs on every pixel of every frame, and allocating three
 * width-by-height arrays at 30 fps is the wrong trade. That is a measured
 * decision, not an oversight. Every indexed read is bounded by `pixels`, which
 * is the buffers' own length, so the reads are asserted non-null.
 */

export type FilterMode = 'off' | 'median' | 'median+time' | 'median+hold'

export const FILTER_MODES: readonly FilterMode[] = [
  'median',
  'median+time',
  'median+hold',
  'off',
]

/**
 * The demo pins the filter to `median+hold`. The other modes are kept because
 * they are how the default was chosen, and re-exposing them is a one-line
 * change in `src/demo/controls.ts` after a device session.
 */
export const DEFAULT_FILTER_MODE: FilterMode = 'median+hold'

/**
 * Beyond this the pixel is looking at something new, so blending would smear.
 *
 * This was 80 mm, which is more than three times the height of every object
 * ever measured here and ~13x the noise floor, so the gate effectively never
 * tripped: the pass blended across whatever the pixel slid onto as the phone
 * moved and performed identically to plain `median` at twice the cost. At 10 mm
 * it sits just above the ~6 mm noise floor, so it rejects a genuine change of
 * surface while still averaging repeated looks at the same one.
 */
const TEMPORAL_RESET_METRES = 0.01
const TEMPORAL_ALPHA = 0.5

/**
 * Per-pixel peak hold: fast attack toward the camera, slow release away from it.
 *
 * ARCore's temporal stage re-blurs a bilateral grid with a [1,4,6,4,1] kernel
 * every frame (Valentin et al. 2018, §7.2 Eq. 14), so held still the blurs
 * compound and small relief melts away — measured here as roughly 60% of an
 * object's height lost over ~60 s. Depth is a *distance*, so relief melting
 * away is the value drifting up; keeping the minimum ever seen at that pixel
 * keeps the tallest look at it instead of the most-blurred one.
 *
 * The release is the leak that stops it being a permanent latch. Lasertag —
 * the only shipping live reconstruction with a working anti-wobble mechanism —
 * caps its per-voxel running average at 16 samples, so 1/17 per frame is that
 * same settle expressed as a rate: a noise spike toward the camera unwinds in a
 * few tenths of a second, while a real object that is there every frame is
 * re-held every frame and never decays.
 */
const HOLD_LEAK = 1 / 17

const MEDIAN_WINDOW = new Float32Array(9)

/**
 * The 3x3 median exists because a single noisy pixel becomes a visible bump in
 * the mesh, and a mean would smear real edges while a median preserves them.
 * The temporal pass is gated rather than a plain EMA: the depth buffer is
 * camera-space, so when the phone moves a given pixel starts looking at
 * something else entirely and blending across that is wrong.
 */
export class DepthFilter {
  private output = new Float32Array(0)
  private scratch = new Float32Array(0)
  private history = new Float32Array(0)

  reset(): void {
    this.history.fill(0)
  }

  apply(
    raw: Uint16Array | Float32Array,
    width: number,
    height: number,
    scale: number,
    mode: FilterMode
  ): Float32Array {
    const pixels = width * height
    if (this.output.length !== pixels) {
      this.output = new Float32Array(pixels)
      this.scratch = new Float32Array(pixels)
      this.history = new Float32Array(pixels)
    }

    const { output, scratch } = this
    for (let i = 0; i < pixels; i += 1) scratch[i] = raw[i]! * scale

    if (mode === 'off') {
      output.set(scratch)
      return output
    }

    median3(scratch, output, width, height)
    if (mode === 'median+time') this.blendWithHistory(output, pixels)
    else if (mode === 'median+hold') this.holdWithHistory(output, pixels)
    return output
  }

  private blendWithHistory(values: Float32Array, pixels: number): void {
    const history = this.history
    for (let i = 0; i < pixels; i += 1) {
      const value = values[i]!
      if (value === 0) {
        history[i] = 0
        continue
      }

      const previous = history[i]!
      const blended =
        previous === 0 || Math.abs(value - previous) > TEMPORAL_RESET_METRES
          ? value
          : previous + (value - previous) * TEMPORAL_ALPHA

      values[i] = blended
      history[i] = blended
    }
  }

  private holdWithHistory(values: Float32Array, pixels: number): void {
    const history = this.history
    for (let i = 0; i < pixels; i += 1) {
      const value = values[i]!
      if (value === 0) {
        history[i] = 0
        continue
      }

      const previous = history[i]!
      let held: number
      if (previous === 0 || Math.abs(value - previous) > TEMPORAL_RESET_METRES) held = value
      else if (value < previous) held = value
      else held = previous + (value - previous) * HOLD_LEAK

      values[i] = held
      history[i] = held
    }
  }
}

/** 3x3 median, skipping invalid neighbours. Exported for the tests. */
export function median3(
  source: Float32Array,
  target: Float32Array,
  width: number,
  height: number
): void {
  for (let y = 0; y < height; y += 1) {
    const yStart = Math.max(0, y - 1)
    const yEnd = Math.min(height - 1, y + 1)

    for (let x = 0; x < width; x += 1) {
      const index = y * width + x
      if (source[index] === 0) {
        target[index] = 0
        continue
      }

      let count = 0
      const xStart = Math.max(0, x - 1)
      const xEnd = Math.min(width - 1, x + 1)
      for (let ny = yStart; ny <= yEnd; ny += 1) {
        const row = ny * width
        for (let nx = xStart; nx <= xEnd; nx += 1) {
          const value = source[row + nx]!
          if (value === 0) continue

          let slot = count
          while (slot > 0 && MEDIAN_WINDOW[slot - 1]! > value) {
            MEDIAN_WINDOW[slot] = MEDIAN_WINDOW[slot - 1]!
            slot -= 1
          }
          MEDIAN_WINDOW[slot] = value
          count += 1
        }
      }

      target[index] = count === 0 ? 0 : MEDIAN_WINDOW[count >> 1]!
    }
  }
}
